import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import RegistrationModal from "@/pages/home/components/RegistrationModal";
import { activityService } from "@/services/activity.service";
import { jobService } from "@/services/job.service";

const companyColors: Record<number, { bg: string; text: string }> = {
  1: { bg: "#EDE9FE", text: "#7C3AED" },
  2: { bg: "#D1FAE5", text: "#065F46" },
  3: { bg: "#FEF9C3", text: "#92400E" },
  4: { bg: "#FEE2E2", text: "#991B1B" },
};

const typeLabels: Record<string, string> = {
  "full-time": "מלאה",
  "part-time": "חלקית",
  freelance: "פרילנס",
};

const typeColors: Record<string, string> = {
  "full-time": "#D1FAE5",
  "part-time": "#FEF9C3",
  freelance: "#EDE9FE",
};

const initials = (name: string) => {
  const words = name.trim().split(" ");
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

export default function ItemDetailsPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [activity, setActivity] = useState<any | null>(null);
  const [job, setJob] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const isJob = itemId?.startsWith("job-");

  useEffect(() => {
    if (!itemId) {
      setLoading(false);
      return;
    }

    if (itemId.startsWith("activity-")) {
      const id = parseInt(itemId.replace("activity-", ""));
      if (isNaN(id)) {
        setLoading(false);
        return;
      }
      activityService
        .get(id)
        .then((data) => {
          setActivity(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else if (itemId.startsWith("job-")) {
      const id = parseInt(itemId.replace("job-", ""));
      if (isNaN(id)) {
        setLoading(false);
        return;
      }
      jobService
        .get(id)
        .then((data) => {
          setJob(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [itemId]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        dir="rtl"
        style={{ background: "#FAF9F6" }}
      >
        <p className="text-gray-500 font-semibold">טוען...</p>
      </div>
    );
  }

  if ((isJob && !job) || (!isJob && !activity)) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-4"
        dir="rtl"
        style={{ background: "#FAF9F6" }}
      >
        <p className="text-xl font-black text-gray-900">הפריט לא נמצא</p>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 rounded-2xl border-2 border-gray-900 px-5 py-2.5 text-sm font-bold bg-white cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line" /> חזרה
        </button>
      </div>
    );
  }

  if (isJob && job) {
    const colors = companyColors[(job.id % 4) + 1] ?? {
      bg: "#F3F4F6",
      text: "#374151",
    };
    const typeLabel = typeLabels[job.type] || job.type;
    const typeBg = typeColors[job.type] || "#F3F4F6";

    return (
      <div
        className="min-h-screen pb-10"
        dir="rtl"
        style={{ background: "#FAF9F6" }}
      >
        <div className="sticky top-0 z-20 flex items-center justify-between px-4 py-3 border-b-2 border-gray-900 bg-white">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 rounded-xl border-2 border-gray-900 px-3 py-1.5 text-sm font-bold bg-white hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line text-base" /> חזרה
          </button>
          <span className="text-sm font-black text-gray-500">משרה</span>
          <div className="w-16" />
        </div>

        <div className="max-w-lg mx-auto px-4 pt-5">
          <div className="flex items-center gap-4 mb-5">
            <div
              className="w-16 h-16 rounded-2xl border-2 border-gray-900 flex items-center justify-center font-black text-xl select-none"
              style={{ background: colors.bg, color: colors.text }}
            >
              {initials(job.company)}
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 leading-snug">
                {job.title}
              </h1>
              <p className="text-sm font-bold text-gray-500 mt-1">
                {job.company}
              </p>
            </div>
          </div>

          <div
            className="rounded-3xl border-[2.5px] border-gray-900 p-4 mb-5 flex flex-col gap-3"
            style={{ background: "#FFFFFF" }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-900"
                style={{ background: colors.bg }}
              >
                <i className="ri-briefcase-line text-gray-900 text-sm" />
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  סוג משרה
                </p>
                <span
                  className="inline-block text-xs font-black border-2 border-gray-900 rounded-full px-2 py-0.5 mt-0.5"
                  style={{ background: typeBg }}
                >
                  {typeLabel}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-900"
                style={{ background: colors.bg }}
              >
                <i className="ri-map-pin-line text-gray-900 text-sm" />
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  מיקום
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {job.location}
                </p>
              </div>
            </div>
            {job.posted_date && (
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-900"
                  style={{ background: colors.bg }}
                >
                  <i className="ri-calendar-line text-gray-900 text-sm" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                    תאריך פרסום
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    {job.posted_date?.split("T")[0]}
                  </p>
                </div>
              </div>
            )}
          </div>

          {job.description && (
            <div
              className="rounded-3xl border-[2.5px] border-gray-900 p-5 mb-6"
              style={{ background: "#FFFFFF" }}
            >
              <h2 className="text-base font-black text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-file-text-line text-gray-500" /> תיאור המשרה
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                {job.description}
              </p>
            </div>
          )}

          {job.requirements && job.requirements.length > 0 && (
            <div
              className="rounded-3xl border-[2.5px] border-gray-900 p-5 mb-6"
              style={{ background: "#FFFFFF" }}
            >
              <h2 className="text-base font-black text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-list-check text-gray-500" /> דרישות
              </h2>
              <ul className="flex flex-col gap-2">
                {job.requirements.map((req: string, i: number) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 flex-shrink-0" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={() => {
              setModalOpen(true);
            }}
            className="w-full rounded-2xl border-[3px] border-gray-900 py-4 text-base font-black text-gray-900 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
            style={{ background: colors.bg }}
          >
            <i className="ri-send-plane-line text-lg" /> הגש מועמדות
          </button>
        </div>

        <RegistrationModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          registerableType="Job"
          registerableId={job?.id}
        />
      </div>
    );
  }

  const typeName = activity.type?.name || "webinar";
  const typeLabel =
    typeName === "webinar"
      ? "וובינר"
      : typeName === "training"
        ? "הכשרה"
        : "סדנה";
  const ctaLabel =
    typeName === "webinar"
      ? "הרשמה לוובינר"
      : typeName === "training"
        ? "הרשמה להכשרה"
        : "הרשמה לסדנה";
  const accentColor =
    typeName === "webinar"
      ? "#EDE9FE"
      : typeName === "training"
        ? "#FEF9C3"
        : "#FEE2E2";

  return (
    <div
      className="min-h-screen pb-10"
      dir="rtl"
      style={{ background: "#FAF9F6" }}
    >
      <div className="sticky top-0 z-20 flex items-center justify-between px-4 py-3 border-b-2 border-gray-900 bg-white">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 rounded-xl border-2 border-gray-900 px-3 py-1.5 text-sm font-bold bg-white hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line text-base" /> חזרה
        </button>
        <span className="text-sm font-black text-gray-500">{typeLabel}</span>
        <div className="w-16" />
      </div>

      <div className="max-w-lg mx-auto px-4 pt-5">
        <div
          className="w-full rounded-3xl border-[3px] border-gray-900 overflow-hidden mb-5"
          style={{ height: 220 }}
        >
          <img
            src={activity.image || ""}
            alt={activity.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span
            className="rounded-full border-2 border-gray-900 px-3 py-1 text-xs font-black"
            style={{ background: "#6EE7B7" }}
          >
            חינמי
          </span>
          <span
            className="rounded-full border-2 border-gray-900 px-3 py-1 text-xs font-bold text-gray-600"
            style={{ background: accentColor }}
          >
            {typeLabel}
          </span>
        </div>

        <h1 className="text-2xl font-black text-gray-900 leading-snug mb-4">
          {activity.title}
        </h1>

        <div
          className="rounded-3xl border-[2.5px] border-gray-900 p-4 mb-5 flex flex-col gap-3"
          style={{ background: "#FFFFFF" }}
        >
          {activity.date && (
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-900"
                style={{ background: accentColor }}
              >
                <i className="ri-calendar-line text-gray-900 text-sm" />
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  תאריך
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {activity.date}
                </p>
              </div>
            </div>
          )}
          {activity.time && (
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-900"
                style={{ background: accentColor }}
              >
                <i className="ri-time-line text-gray-900 text-sm" />
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  שעה
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {activity.time}
                </p>
              </div>
            </div>
          )}
          {activity.location && (
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-900"
                style={{ background: accentColor }}
              >
                <i className="ri-map-pin-line text-gray-900 text-sm" />
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  מיקום
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {activity.location}
                </p>
              </div>
            </div>
          )}
        </div>

        {activity.description && (
          <div
            className="rounded-3xl border-[2.5px] border-gray-900 p-5 mb-6"
            style={{ background: "#FFFFFF" }}
          >
            <h2 className="text-base font-black text-gray-900 mb-3 flex items-center gap-2">
              <i className="ri-file-text-line text-gray-500" /> אודות
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {activity.description}
            </p>
          </div>
        )}

        <button
          onClick={() => setModalOpen(true)}
          className="w-full rounded-2xl border-[3px] border-gray-900 py-4 text-base font-black text-gray-900 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          style={{ background: accentColor }}
        >
          <i className="ri-user-add-line text-lg" /> {ctaLabel}
        </button>
      </div>

      <RegistrationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        registerableType="Activity"
        registerableId={activity?.id}
      />
    </div>
  );
}
